/**
 * SecretsPage (epic-6 §3 / SEC) — vault CRUD and entries management.
 * Entries table shows KEYS ONLY; values are only ever written via the "set
 * value" dialog (password input) and are never displayed back — the backend
 * exposes values only through an explicit per-key POST /read.
 */
import { useState } from 'react';
import { Plus, Pencil, Trash2, KeyRound, EyeOff, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetVaultsQuery,
  useGetVaultQuery,
  useCreateVaultMutation,
  useUpdateVaultMutation,
  useDeleteVaultMutation,
  useSaveVaultEntryMutation,
} from '@/store/api/secretsApi';
import type { Vault, VaultEntryRef } from '@/types';
import { format } from 'date-fns';

export function SecretsPage() {
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Vault | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Vault | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [entryTarget, setEntryTarget] = useState<{ vault: Vault; key: string } | null>(null);
  const [form, setForm] = useState({ name: '', description: '', allowAgentRead: true });

  const { data: vaults, isLoading } = useGetVaultsQuery();
  const { data: activeVault } = useGetVaultQuery(selected ?? '', { skip: !selected });
  const [createVault] = useCreateVaultMutation();
  const [updateVault] = useUpdateVaultMutation();
  const [deleteVault, { isLoading: isDeleting }] = useDeleteVaultMutation();
  const [saveEntry, { isLoading: isSavingEntry }] = useSaveVaultEntryMutation();

  const vaultList = (vaults as Vault[] | undefined) ?? [];
  const effVault = activeVault ?? vaultList.find((v) => v._id === selected);
  const entries: VaultEntryRef[] = (effVault?.entries ?? []) as VaultEntryRef[];

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    const body = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      settings: { allowAgentRead: form.allowAgentRead },
    };
    try {
      if (editing) {
        await updateVault({ id: editing._id, body }).unwrap();
      } else {
        await createVault(body).unwrap();
      }
      setModalOpen(false);
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to save vault');
    }
  }

  const [entryValue, setEntryValue] = useState('');
  const [newKeyName, setNewKeyName] = useState('');

  function openCreate() {
    setEditing(null);
    setForm({ name: '', description: '', allowAgentRead: true });
    setModalOpen(true);
  }

  function openEdit(vault: Vault) {
    setEditing(vault);
    setForm({
      name: vault.name,
      description: vault.description ?? '',
      allowAgentRead: vault.settings?.allowAgentRead ?? true,
    });
    setModalOpen(true);
  }

  async function saveEntryWithKey(target: { vault: Vault; key: string }) {
    const key = target.key || newKeyName.trim();
    if (!key) {
      toast.error('Key is required');
      return;
    }
    try {
      await saveEntry({ id: target.vault._id, body: { key, value: entryValue } }).unwrap();
      toast.success(`Value set for "${key}"`);
      setEntryTarget(null);
      setEntryValue('');
      setNewKeyName('');
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to set value');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Secrets</h1>
          <p className="text-sm text-muted-foreground">
            Encrypted vaults — keys are listed; values are write-only
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Create Vault
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        {/* Vault list */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Vaults</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            {isLoading ? (
              <LoadingSpinner className="py-8" />
            ) : vaultList.length === 0 ? (
              <EmptyState title="No vaults" description="Create a vault to store secrets" />
            ) : (
              <div className="space-y-1">
                {vaultList.map((v) => (
                  <div
                    key={v._id}
                    className={`flex cursor-pointer items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors ${selected === v._id ? 'border-primary bg-primary/5' : 'hover:bg-muted/40'}`}
                    onClick={() => setSelected(v._id)}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate font-medium">{v.name}</span>
                    </div>
                    <Badge variant="outline" className="text-[10px] shrink-0">{v.entries?.length ?? 0}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selected vault entries */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-base">{effVault?.name ?? 'Select a vault'}</CardTitle>
              <p className="text-xs text-muted-foreground">
                {effVault
                  ? `${effVault.description ?? 'No description'} · ${entries.length} key(s)`
                  : 'Keys are shown; values are never displayed (SEC).'}
              </p>
            </div>
            {effVault && (
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(effVault)} title="Edit vault">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(effVault)} title="Delete vault">
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="pt-2">
            {!effVault ? (
              <EmptyState title="No vault selected" description="Pick a vault from the left to manage its entries" />
            ) : entries.length === 0 ? (
              <EmptyState
                title="No entries"
                description="Set the first secret value for this vault"
                action={
                  <Button onClick={() => { setEntryTarget({ vault: effVault, key: '' }); setEntryValue(''); }}>
                    <Plus className="mr-1 h-4 w-4" /> Set secret
                  </Button>
                }
              />
            ) : (
              <div className="space-y-1.5">
                {entries.map((entry) => (
                  <div key={entry.key} className="flex items-center justify-between rounded-md border px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="font-mono text-sm font-medium">{entry.key}</span>
                      <span title="value hidden">
                        <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground">
                        updated {entry.updatedAt ? format(new Date(entry.updatedAt), 'MMM d, HH:mm') : '—'}
                      </span>
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setEntryTarget({ vault: effVault, key: entry.key }); setEntryValue(''); }}>
                        Set value
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create / edit vault */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Vault' : 'Create Vault'}</DialogTitle>
            <DialogDescription>Vault metadata — entries are managed from the vault panel.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                className="flex min-h-[70px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <label className="flex items-start gap-2 rounded-md border p-3 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.allowAgentRead}
                onChange={(e) => setForm({ ...form, allowAgentRead: e.target.checked })}
              />
              <span>
                Allow agent / service reads
                <span className="block text-xs text-muted-foreground">
                  Required for internal services that fetch secrets over the backplane (e.g.
                  auth-service reading its MFA key). Leave off for user-only secrets.
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set value dialog — the ONLY place a value is written; never shown back */}
      <Dialog open={!!entryTarget} onOpenChange={(open) => !open && setEntryTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set secret value</DialogTitle>
            <DialogDescription>
              Enter the value for key "{entryTarget?.key || 'new key'}". The value is encrypted and will never be displayed again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!entryTarget?.key && (
              <div className="space-y-2">
                <Label>Key</Label>
                <Input placeholder="e.g. GITHUB_TOKEN" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} className="font-mono" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Value</Label>
              <Input type="password" autoComplete="new-password" value={entryValue} onChange={(e) => setEntryValue(e.target.value)} className="font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntryTarget(null)}>Cancel</Button>
            <Button onClick={() => entryTarget && saveEntryWithKey(entryTarget)} disabled={isSavingEntry || !entryValue || (!entryTarget?.key && !newKeyName.trim())}>
              {isSavingEntry ? 'Saving…' : 'Set value'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Vault"
        description={`Delete vault "${deleteTarget?.name}" and ALL its secrets? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={async () => {
          if (deleteTarget) {
            try {
              await deleteVault(deleteTarget._id).unwrap();
              if (selected === deleteTarget._id) setSelected(null);
              setDeleteTarget(null);
            } catch (err) {
              toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to delete vault');
            }
          }
        }}
        isLoading={isDeleting}
      />
    </div>
  );
}